// Documents through the Asisten AI (docs/contract.md section 10): the
// message kinds, the proposal payload, the create-document call, and the
// one-time handoff of field values to Buat Dokumen.
//
// The shapes extend ChatMessage with optional fields, so this compiles both
// before and after the API's AppType gains kind, proposal and documentId (an
// intersection with the API's required fields stays the API's type).
import type { ChatMessage, DocumentDetail } from "../api";
import * as API from "../api";
import { api } from "../client";

export type MessageKind = "text" | "document_proposal" | "document_created";

export interface DocumentProposal {
  tahap: string;
  docType: string;
  /** null when the assistant could not tell; the user picks it on the card. */
  subCategory: string | null;
  title?: string | null;
  input: Record<string, string>;
  missing?: string[] | null;
  instructions?: string | null;
}

export type ThreadMessage = ChatMessage & {
  kind?: MessageKind;
  proposal?: DocumentProposal | null;
  documentId?: string | null;
};

/** The proposal as the thread carries it (the API shape once AppType has it). */
export type ThreadProposal = NonNullable<ThreadMessage["proposal"]>;

export function messageKind(m: ThreadMessage): MessageKind {
  if (m.kind === "document_proposal" && m.proposal) return "document_proposal";
  if (m.kind === "document_created") return "document_created";
  return "text";
}

export interface CreateDocumentInput {
  input: Record<string, string>;
  /** Only when the proposal had none and the user picked it on the card. */
  subCategory?: string;
  title?: string;
  instructions?: string;
}

export interface CreateDocumentResult {
  document: DocumentDetail;
  message: ThreadMessage;
}

// The route through the typed client. Until AppType carries it, a structural
// stand-in keeps the call compiling; once it does, the real route type is used
// and the request body is checked against it.
type MessagesRoute = (typeof api.chat.conversations)[":id"]["messages"];
type RealRoute = MessagesRoute extends { ":messageId": { "create-document": infer R } } ? R : never;
interface StandInRoute {
  $post(args: { param: { id: string; messageId: string }; json: CreateDocumentInput }): Promise<Response>;
}
type CreateRoute = [RealRoute] extends [never] ? StandInRoute : RealRoute;
type Poster = { $post(args: { param: { id: string; messageId: string }; json: CreateDocumentInput }): Promise<Response> };

const FALLBACK: Record<number, string> = {
  0: "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.",
  403: "Anda tidak memiliki akses untuk membuat dokumen ini.",
  409: "Usulan ini sudah dipakai untuk membuat dokumen.",
  422: "Isian belum lengkap atau tidak sesuai katalog.",
  429: "Batas penggunaan tercapai. Tunggu sebentar, lalu coba lagi.",
  502: "Pluto sedang sibuk, coba lagi sebentar.",
};

/** POST /chat/conversations/:id/messages/:messageId/create-document. */
export async function createDocumentFromProposal(conversationId: string, messageId: string, body: CreateDocumentInput): Promise<CreateDocumentResult> {
  const route = (api.chat.conversations[":id"].messages as unknown as { ":messageId": { "create-document": CreateRoute } })[":messageId"]["create-document"] as unknown as Poster;
  let res: Response;
  try {
    res = await route.$post({ param: { id: conversationId, messageId }, json: body });
  } catch {
    throw new API.ApiError(0, FALLBACK[0]!);
  }
  if (!res.ok) {
    let message = FALLBACK[res.status] ?? "Terjadi kesalahan. Coba lagi.";
    try {
      const err = (await res.json()) as { error?: unknown } | null;
      if (err && typeof err.error === "string" && err.error.trim()) message = err.error;
      // A newer User Agreement waits: the app moves to the agreement step.
      if (API.isAgreementRequired(res.status, err)) API.notifyAgreementRequired();
    } catch {
      // Not JSON: keep the fallback for this status.
    }
    // A 401 returns the app to Masuk through the shared handler in api.ts.
    if (res.status === 401) void API.me().catch(() => undefined);
    throw new API.ApiError(res.status, message);
  }
  return (await res.json()) as CreateDocumentResult;
}

// ---------------------------------------------------------------------------
// Ubah di Buat Dokumen: tahap, jenis and sub kategori travel in the query
// string; the field values never do. They wait in sessionStorage under one
// key, read once by Buat Dokumen and removed at once.
// ---------------------------------------------------------------------------
export const PREFILL_KEY = "procureai.buat.prefill";
const PREFILL_TTL_MS = 10 * 60_000;

export interface Prefill {
  docType: string;
  subCategory: string;
  input: Record<string, string>;
  title: string;
  instructions: string;
  at: number;
}

function storage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function savePrefill(p: Omit<Prefill, "at">): void {
  try {
    storage()?.setItem(PREFILL_KEY, JSON.stringify({ ...p, at: Date.now() }));
  } catch {
    // Storage full or blocked: Buat Dokumen still opens on the right step.
  }
}

/** Reads and removes the handoff. Only a fresh one for this jenis dokumen counts. */
export function takePrefill(docType: string | undefined): Prefill | null {
  const s = storage();
  if (!s) return null;
  let raw: string | null = null;
  try {
    raw = s.getItem(PREFILL_KEY);
    s.removeItem(PREFILL_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<Prefill> | null;
    if (!p || typeof p !== "object" || typeof p.docType !== "string" || p.docType !== docType) return null;
    if (typeof p.at !== "number" || Date.now() - p.at > PREFILL_TTL_MS) return null;
    const input: Record<string, string> = {};
    for (const [k, v] of Object.entries(p.input ?? {})) if (typeof v === "string") input[k] = v;
    return {
      docType: p.docType,
      subCategory: typeof p.subCategory === "string" ? p.subCategory : "",
      input,
      title: typeof p.title === "string" ? p.title : "",
      instructions: typeof p.instructions === "string" ? p.instructions : "",
      at: p.at,
    };
  } catch {
    return null;
  }
}

/** The /buat address for a proposal: ids only. */
export function buatHref(p: Pick<DocumentProposal, "tahap" | "docType" | "subCategory">): string {
  const q = new URLSearchParams({ t: p.tahap, j: p.docType });
  if (p.subCategory) q.set("s", p.subCategory);
  return `/buat?${q.toString()}`;
}
